import {
  formatMoney,
  formatQuantity,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@platform/ui";
import { localName } from "@platform/ui/features";
import { useTranslations } from "use-intl";
import { categoryPath, type OpsCategory, type ProductDraft } from "../lib/catalog";
import { locale } from "../lib/i18n";

/**
 * A listing as its public page would show it (card 044), for an operator deciding on it: images with alt text,
 * text in both languages, the category, attributes by their labels, the kind's fields, and SKUs with tiers.
 */
export function ProductPreview({
  content,
  categories,
  images,
}: {
  content: ProductDraft;
  categories: readonly OpsCategory[];
  images: readonly { fileId: string; url: string }[];
}) {
  const t = useTranslations();
  const regions = new Intl.DisplayNames([locale === "zh" ? "zh-CN" : "en"], { type: "region" });
  const category = categories.find((each) => each.id === content.categoryId);
  const urls = new Map(images.map((image) => [image.fileId, image.url]));
  const attributeText = (key: string, value: ProductDraft["attributes"][string]): string => {
    const definition = category?.attributeSchema?.attributes.find((each) => each.key === key);
    if (typeof value === "boolean") return t(value ? "catalog.yes" : "catalog.no");
    if (definition?.type === "choice") {
      const label = (option: string) =>
        localName(definition.options.find((each) => each.value === option)?.label, locale) || option;
      return Array.isArray(value) ? value.map(label).join(", ") : label(String(value));
    }
    const text = Array.isArray(value) ? value.join(", ") : String(value);
    return definition?.type === "number" && definition.unit !== null ? `${text} ${definition.unit}` : text;
  };
  const attributeLabel = (key: string) =>
    localName(category?.attributeSchema?.attributes.find((each) => each.key === key)?.label, locale) || key;
  const facts: [string, string][] = [
    [t("admin.preview.category"), categoryPath(categories, content.categoryId)],
    [t("admin.preview.availability"), t(`catalog.availability.${content.availability}`)],
    ...(content.leadTimeDays === null
      ? []
      : ([[t("admin.preview.leadTime"), String(content.leadTimeDays)]] as [string, string][])),
    ...(content.hsCode === null ? [] : ([[t("admin.preview.hsCode"), content.hsCode]] as [string, string][])),
    ...(content.originCountry === null
      ? []
      : ([[t("admin.preview.origin"), regions.of(content.originCountry) ?? content.originCountry]] as [
          string,
          string,
        ][])),
    ...(content.serviceRegions === null
      ? []
      : ([
          [
            t("admin.preview.regions"),
            content.serviceRegions.map((code) => regions.of(code) ?? code).join(", "),
          ],
        ] as [string, string][])),
    ...(content.availableQuantity === null
      ? []
      : ([[t("admin.preview.quantity"), formatQuantity(content.availableQuantity, locale)]] as [
          string,
          string,
        ][])),
    ...(content.sample === null
      ? []
      : ([[t("admin.preview.sample"), formatMoney(content.sample, locale)]] as [string, string][])),
    ...Object.entries(content.attributes).map(
      ([key, value]) => [attributeLabel(key), attributeText(key, value)] as [string, string],
    ),
  ];
  return (
    <div className="flex flex-col gap-4 text-small">
      {content.media.length > 0 && (
        <ul className="flex flex-wrap gap-3">
          {content.media.map((item) => (
            <li key={item.fileId} className="flex w-40 flex-col gap-1">
              {urls.has(item.fileId) ? (
                <img
                  src={urls.get(item.fileId)}
                  alt={localName(item.alt, locale)}
                  className="h-28 w-40 rounded object-cover"
                />
              ) : (
                <div className="h-28 w-40 rounded bg-muted" />
              )}
              <span className="text-caption text-ink-muted">
                {localName(item.alt, locale) || t("admin.preview.noAlt")}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-col gap-1">
        <h2 className="text-heading font-semibold text-ink">{content.name.en}</h2>
        {content.name.zh !== undefined && <p className="text-ink-muted">{content.name.zh}</p>}
      </div>
      {content.description !== null && (
        <div className="flex flex-col gap-2 whitespace-pre-line">
          <p>{content.description.en}</p>
          {content.description.zh !== undefined && <p className="text-ink-muted">{content.description.zh}</p>}
        </div>
      )}
      <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[max-content_1fr]">
        {facts.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-ink-muted">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t("admin.preview.sku")}</TableHeaderCell>
            <TableHeaderCell>{t("admin.preview.moq")}</TableHeaderCell>
            <TableHeaderCell>{t("admin.preview.prices")}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {content.skus.map((sku) => (
            <TableRow key={sku.code} className="align-top">
              <TableCell>
                {[sku.code, ...Object.entries(sku.options).map(([name, value]) => `${name}: ${value}`)].join(
                  " · ",
                )}
              </TableCell>
              <TableCell>{`${formatQuantity(sku.moq, locale)} ${sku.unit}`}</TableCell>
              <TableCell>
                {content.inquiryOnly
                  ? t("admin.preview.inquiryOnly")
                  : sku.tiers
                      .map((tier) =>
                        t("admin.preview.tier", {
                          quantity: formatQuantity(tier.minQuantity, locale),
                          price: formatMoney(tier.unitPrice, locale),
                        }),
                      )
                      .join("; ")}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
